import {V2TranscriptWord} from "./index";

type Sentence = { words: V2TranscriptWord[]; startSec: number; endSec: number };
type Paragraph = { sentences: Sentence[]; startSec: number; endSec: number };

// Segment words → sentences using timing + punctuation
export function wordsToSentences(words: V2TranscriptWord[]): Sentence[] {
    const sentences: Sentence[] = [];
    let current: V2TranscriptWord[] = [];

    const isSentenceEndToken = (w: V2TranscriptWord) =>
        !!w.text && (/[.!?]$/.test(w.text) || /^[.!?]$/.test(w.text));

    const SENTENCE_GAP = 1.0;          // seconds
    const MIN_SENTENCE_WORDS = 4;
    const MAX_SENTENCE_WORDS = 50;

    for (let i = 0; i < words.length; i++) {
        const w = words[i];
        current.push(w);

        const next = words[i + 1];
        const wEnd = (w.endSec ?? w.startSec ?? 0);
        const nStart = (next?.startSec ?? wEnd);
        const gap = Math.max(0, nStart - wEnd);

        const endsWithPunct = isSentenceEndToken(w);
        const longGapBreak =
            !!next &&
            gap >= SENTENCE_GAP &&
            current.length >= MIN_SENTENCE_WORDS;
        const tooLong = current.length >= MAX_SENTENCE_WORDS;

        if (!next || endsWithPunct || longGapBreak || tooLong) {
            const sStart = current[0]?.startSec ?? 0;
            const sEnd = current[current.length - 1]?.endSec ?? current[current.length - 1]?.startSec ?? sStart;
            sentences.push({ words: current, startSec: sStart, endSec: sEnd });
            current = [];
        }
    }
    return sentences;
}

// Group sentences → paragraphs using gaps and soft cues
export function sentencesToParagraphs(sentences: Sentence[]): Paragraph[] {
    const paras: Paragraph[] = [];
    let current: Sentence[] = [];

    const HARD_GAP = 3.0;
    const SOFT_GAP = 1.8;
    const MIN_PARA_SEC = 10;
    const MAX_PARA_SENT = 5;

    const resetCue = /^((now|so|anyway|first|second|third|next)\b|the first thing\b)/i;

    const sentenceText = (s: Sentence) => s.words.map(w => w.text).join(" ").trim();
    const endsWithPunct = (s: Sentence) => /[.!?]["’”]?$/.test(sentenceText(s));
    const paraDuration = (p: Sentence[]) => p.length === 0 ? 0 : (p[p.length - 1].endSec - p[0].startSec);

    for (let i = 0; i < sentences.length; i++) {
        const s = sentences[i];
        current.push(s);

        const next = sentences[i + 1];
        if (!next) break;

        const gap = next.startSec - s.endSec;
        const duration = paraDuration(current);
        const nextText = sentenceText(next);

        const shouldBreak =
            gap >= HARD_GAP ||
            (gap >= SOFT_GAP && duration >= MIN_PARA_SEC) ||
            (current.length >= MAX_PARA_SENT && endsWithPunct(s)) ||
            (current.length >= 2 && resetCue.test(nextText));

        if (shouldBreak) {
            const pStart = current[0].startSec;
            const pEnd = current[current.length - 1].endSec;
            paras.push({ sentences: current, startSec: pStart, endSec: pEnd });
            current = [];
        }
    }

    if (current.length) {
        const pStart = current[0].startSec;
        const pEnd = current[current.length - 1].endSec;
        paras.push({ sentences: current, startSec: pStart, endSec: pEnd });
    }

    return paras;
}